// src/pages/settings/Users.jsx
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import api from '../../config/api';
import { Header, Modal, Field, Actions, asArray } from './SettingsUI';

const ROLES = ['admin', 'manager', 'cashier', 'viewer', 'driver'];

export default function Users() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [editing, setEditing] = useState(null);
  const [pwUser, setPwUser] = useState(null);

  const { data: list = [], isLoading } = useQuery(['users'], () =>
    api.get('/api/users').then((r) => asArray(r.data)));
  const { data: locations = [] } = useQuery(['locations'], () =>
    api.get('/api/locations').then((r) => asArray(r.data)));

  const save = useMutation(
    (b) => b._id
      ? api.put(`/api/users/${b._id}`, { name: b.name, email: b.email, role: b.role, status: b.status, location_id: b.location_id || null })
      : api.post('/api/users', { name: b.name, email: b.email, password: b.password, role: b.role, location_id: b.location_id || null }),
    { onSuccess: () => { qc.invalidateQueries(['users']); setEditing(null); }, onError: (e) => alert(e?.response?.data?.message || e.message) });
  const del = useMutation((id) => api.delete(`/api/users/${id}`),
    { onSuccess: () => qc.invalidateQueries(['users']), onError: (e) => alert(e?.response?.data?.message || e.message) });
  const setPw = useMutation(
    ({ id, password }) => api.put(`/api/users/${id}/password`, { password }),
    { onSuccess: () => setPwUser(null), onError: (e) => alert(e?.response?.data?.message || e.message) });

  return (
    <div className="p-5">
      <Header title={t('settings.users', 'Users')} onAdd={() => setEditing({})} addLabel={t('common.add', 'Add')} />
      {isLoading ? <div className="text-gray-400 py-8">…</div> : !list.length ? <div className="text-gray-400 py-8">{t('common.none', 'Nothing yet')}</div> : (
        <div className="bg-white rounded-xl border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-500"><tr>
              <th className="text-start px-4 py-2">{t('common.name', 'Name')}</th>
              <th className="text-start px-4 py-2">{t('common.email', 'Email')}</th>
              <th className="text-start px-4 py-2">{t('common.role', 'Role')}</th>
              <th className="text-start px-4 py-2">{t('common.status', 'Status')}</th>
              <th className="px-4 py-2 w-44"></th>
            </tr></thead>
            <tbody>
              {list.map((r) => (
                <tr key={r._id} className="border-t">
                  <td className="px-4 py-2 font-semibold">{r.name}</td>
                  <td className="px-4 py-2 text-gray-600">{r.email}</td>
                  <td className="px-4 py-2">{r.role}</td>
                  <td className="px-4 py-2">{r.status || 'active'}</td>
                  <td className="px-4 py-2 text-end whitespace-nowrap">
                    <button onClick={() => setEditing(r)} className="text-blue-600 font-semibold me-3">{t('common.edit', 'Edit')}</button>
                    <button onClick={() => setPwUser(r)} className="text-amber-600 font-semibold me-3">{t('users.password', 'Password')}</button>
                    <button onClick={() => window.confirm(t('common.confirmDelete', 'Delete this user?')) && del.mutate(r._id)} className="text-red-600 font-semibold">{t('common.delete', 'Delete')}</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && <UserForm row={editing} roles={ROLES} locations={locations} t={t} busy={save.isLoading}
                            onClose={() => setEditing(null)} onSave={(b) => save.mutate(b)} />}
      {pwUser && <PwModal user={pwUser} t={t} busy={setPw.isLoading}
                          onClose={() => setPwUser(null)} onSave={(pw) => setPw.mutate({ id: pwUser._id, password: pw })} />}
    </div>
  );
}

function UserForm({ row, roles, locations, onClose, onSave, busy, t }) {
  const isNew = !row._id;
  const [f, setF] = useState({
    name: row.name || '', email: row.email || '', password: '',
    role: row.role || 'cashier', status: row.status || 'active', location_id: row.location_id || '',
  });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const valid = f.name && f.email && (!isNew || f.password.length >= 4) && f.role;
  return (
    <Modal title={isNew ? t('users.addUser', 'Add user') : t('common.edit', 'Edit')} onClose={onClose}>
      <div className="space-y-3">
        <Field label={t('common.name', 'Name')}><input value={f.name} onChange={set('name')} className="inp" /></Field>
        <Field label={t('common.email', 'Email')}><input type="email" value={f.email} onChange={set('email')} className="inp" /></Field>
        {isNew && <Field label={t('common.password', 'Password')}><input type="password" value={f.password} onChange={set('password')} className="inp" autoComplete="new-password" /></Field>}
        <Field label={t('common.role', 'Role')}>
          <select value={f.role} onChange={set('role')} className="inp">{roles.map((x) => <option key={x} value={x}>{x}</option>)}</select>
        </Field>
        {!isNew && (
          <Field label={t('common.status', 'Status')}>
            <select value={f.status} onChange={set('status')} className="inp"><option value="active">active</option><option value="inactive">inactive</option></select>
          </Field>
        )}
        <Field label={t('common.location', 'Location (optional)')}>
          <select value={f.location_id} onChange={set('location_id')} className="inp">
            <option value="">{t('users.allLocations', 'All locations')}</option>
            {locations.map((l) => <option key={l._id} value={l._id}>{l.name}</option>)}
          </select>
        </Field>
        <Actions busy={busy} disabled={!valid} onClose={onClose} onSave={() => onSave({ ...row, ...f })}
                 cancelLabel={t('common.cancel', 'Cancel')} saveLabel={t('common.save', 'Save')} savingLabel={t('common.saving', 'Saving…')} />
      </div>
    </Modal>
  );
}

function PwModal({ user, onClose, onSave, busy, t }) {
  const [pw, setPw] = useState('');
  return (
    <Modal title={`${t('users.password', 'Password')} — ${user.name}`} onClose={onClose}>
      <div className="space-y-3">
        <Field label={t('users.newPassword', 'New password')}>
          <input type="password" value={pw} onChange={(e) => setPw(e.target.value)} className="inp" autoComplete="new-password" />
        </Field>
        <Actions busy={busy} disabled={pw.length < 4} onClose={onClose} onSave={() => onSave(pw)}
                 cancelLabel={t('common.cancel', 'Cancel')} saveLabel={t('users.setPassword', 'Set password')} savingLabel={t('common.saving', 'Saving…')} />
      </div>
    </Modal>
  );
}